import type { MeshTransform } from "./schema";

export const IDENTITY_TRANSFORM: MeshTransform = {
  pos: [0, 0, 0],
  quat: [1, 0, 0, 0],
};

function normalizeQuat(q: [number, number, number, number]): [number, number, number, number] {
  const [w, x, y, z] = q;
  const n = Math.hypot(w, x, y, z);
  if (n < 1e-10) return [1, 0, 0, 0];
  return [w / n, x / n, y / n, z / n];
}

function quatMul(
  a: [number, number, number, number],
  b: [number, number, number, number]
): [number, number, number, number] {
  const [aw, ax, ay, az] = a;
  const [bw, bx, by, bz] = b;
  return [
    aw * bw - ax * bx - ay * by - az * bz,
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
  ];
}

function quatConjugate(q: [number, number, number, number]): [number, number, number, number] {
  return [q[0], -q[1], -q[2], -q[3]];
}

function rotateVecByQuat(
  v: [number, number, number],
  q: [number, number, number, number]
): [number, number, number] {
  const qn = normalizeQuat(q);
  const p: [number, number, number, number] = [0, v[0], v[1], v[2]];
  const qr = quatMul(quatMul(qn, p), quatConjugate(qn));
  return [qr[1], qr[2], qr[3]];
}

export function invertTransform(t: MeshTransform): MeshTransform {
  const q = normalizeQuat(t.quat);
  const qi = quatConjugate(q);
  const pInv = rotateVecByQuat([-t.pos[0], -t.pos[1], -t.pos[2]], qi);
  return { pos: pInv, quat: qi };
}

export function composeTransforms(a: MeshTransform, b: MeshTransform): MeshTransform {
  const qa = normalizeQuat(a.quat);
  const qb = normalizeQuat(b.quat);
  const q = normalizeQuat(quatMul(qa, qb));
  const rb = rotateVecByQuat(b.pos, qa);
  return {
    pos: [a.pos[0] + rb[0], a.pos[1] + rb[1], a.pos[2] + rb[2]],
    quat: q,
  };
}

export function sanitizeTransform(input?: Partial<MeshTransform> | null): MeshTransform {
  const pos = input?.pos;
  const quat = input?.quat;
  return {
    pos:
      Array.isArray(pos) && pos.length === 3
        ? [Number(pos[0]) || 0, Number(pos[1]) || 0, Number(pos[2]) || 0]
        : [0, 0, 0],
    quat: normalizeQuat(
      Array.isArray(quat) && quat.length === 4
        ? [Number(quat[0]) || 1, Number(quat[1]) || 0, Number(quat[2]) || 0, Number(quat[3]) || 0]
        : [1, 0, 0, 0]
    ),
  };
}
